// Small IndexedDB primitive shared by recovery snapshots and the clinical
// repository. Encryption happens outside transactions; compare-and-swap keeps
// read/modify/write operations safe when two tabs edit the same resource.
const DATABASE = 'sena-clinical-local';
const STORE = 'records';

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore(STORE, { keyPath: 'key' });
      store.createIndex('owner', 'owner');
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Close the older Sena tab to finish upgrading device storage.'));
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
  });
}

export async function readStoredRecord(key) {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE).objectStore(STORE).get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

export async function listStoredRecords(owner, kind) {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE).objectStore(STORE).index('owner').getAll(owner);
      request.onsuccess = () => resolve((request.result || []).filter(record => !kind || record.kind === kind));
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

// null removes a record. Returns false if another tab committed first.
export async function compareAndSwapRecord(key, expectedRevision, replacement) {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      let written = false;
      const request = store.get(key);
      request.onsuccess = () => {
        if ((request.result?.storageRevision || 0) !== expectedRevision) return;
        if (replacement === null) store.delete(key);
        else store.put({ ...replacement, key, storageRevision: expectedRevision + 1 });
        written = true;
      };
      tx.oncomplete = () => resolve(written);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('Device save was interrupted.'));
    });
  } finally { db.close(); }
}

export async function storageKey(owner, kind, identity) {
  const bytes = new TextEncoder().encode(JSON.stringify([owner, kind, identity]));
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function newOperationId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}
